import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Store, Users, CreditCard, Wallet, BarChart3,
  Settings, Menu, X, LogOut, ChevronDown,
  Package, Sliders, User, MessageSquare,
  Tag, Ticket, Lock, AlertTriangle, Key, Bell,
  FileText, Star, Mail, Building2, Briefcase, Shield,
  Image as ImageIcon
} from 'lucide-react';
import superAdminService from '../../services/superadmin/superAdminService';
import adminService from '../../services/admin/adminService';
import { getImageUrl } from '../../utils/imageHelper';

interface AdminLayoutProps {
  role?: 'admin' | 'superadmin';
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ role = 'admin' }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [dropdowns, setDropdowns] = useState<Record<string, boolean>>({
    userBusiness: false,
    systemCatalog: false,
    finance: false,
    promotions: false,
    cms: false,
    security: false,
    support: false,
    system: false,
  });
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [userName, setUserName] = useState<string>('');
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef<HTMLDivElement>(null);

  const basePath = role === 'superadmin' ? '/super-admin' : '/admin';

  // --- Sidebar Menu Lists ---
  const superAdminMenuItems = [
    { icon: <LayoutDashboard size={20} />, label: 'Dashboard', path: `${basePath}/dashboard` },
    { icon: <Users size={20} />, label: 'Admin Management', path: `${basePath}/admin-management` },
    { icon: <Shield size={20} />, label: 'Super Admin Profile', path: `${basePath}/profile` },
  ];

  const userBusinessItems = [
    { icon: <Users size={20} />, label: 'Users', path: `${basePath}/users` },
    { icon: <Store size={20} />, label: 'Businesses', path: `${basePath}/businesses` },
    { icon: <FileText size={20} />, label: 'Business Documents', path: `${basePath}/business-documents` },
  ];

  const systemCatalogItems = [
    { icon: <Package size={20} />, label: 'Master Catalog', path: `${basePath}/master-catalog` },
    { icon: <Package size={20} />, label: 'Categories', path: `${basePath}/categories` },
  ];

  const financeItems = [
    { icon: <CreditCard size={20} />, label: 'Payments', path: `${basePath}/payments` },
    { icon: <Wallet size={20} />, label: 'Withdraw Requests', path: `${basePath}/wallet` },
    { icon: <CreditCard size={20} />, label: 'Deposit Requests', path: `${basePath}/wallet-requests` },
    { icon: <Building2 size={20} />, label: 'Admin Bank Accounts', path: `${basePath}/bank-accounts` },
    { icon: <Sliders size={20} />, label: 'Payment Methods', path: `${basePath}/payment-methods` },
  ];

  const promotionsItems = [
    { icon: <Tag size={20} />, label: 'Offers', path: `${basePath}/offers` },
    { icon: <Ticket size={20} />, label: 'Coupons', path: `${basePath}/coupons` },
  ];

  const cmsItems = [
    { icon: <FileText size={20} />, label: 'Blogs', path: `${basePath}/blogs` },
    { icon: <Star size={20} />, label: 'Testimonials', path: `${basePath}/testimonials` },
    { icon: <Mail size={20} />, label: 'Contact Submissions', path: `${basePath}/contact-submissions` },
    { icon: <Briefcase size={20} />, label: 'Job Openings', path: `${basePath}/jobs` },
    { icon: <Users size={20} />, label: 'Job Applications', path: `${basePath}/job-applications` },
  ];

  const securityItems = [
    { icon: <Lock size={20} />, label: 'Security Logs', path: `${basePath}/security` },
    { icon: <Key size={20} />, label: 'Roles & Permissions', path: `${basePath}/roles` },
    { icon: <AlertTriangle size={20} />, label: 'Fraud Detection', path: `${basePath}/fraud` },
    { icon: <Key size={20} />, label: 'OTP Security', path: `${basePath}/otp-security` },
  ];

  const supportItems = [
    { icon: <MessageSquare size={20} />, label: 'Tickets & Disputes', path: `${basePath}/support` },
  ];

  const systemItems = [
    { icon: <BarChart3 size={20} />, label: 'Reports & Analytics', path: `${basePath}/reports` },
    { icon: <Settings size={20} />, label: 'App Settings', path: `${basePath}/settings` },
  ];

  const menuItems = superAdminMenuItems;

  // Helper function to check if route is active
  const isActive = (path: string) => {
    if (path === `${basePath}/reports`) {
      return location.pathname === `${basePath}/reports` || location.pathname === `${basePath}/analytics`;
    }
    return location.pathname === path;
  };

  // Helper function to check if any item in a dropdown list is active
  const isDropdownActive = (items: Array<{ path: string }>) => {
    return items.some(item => {
      if (item.path === `${basePath}/reports`) {
        return location.pathname === `${basePath}/reports` || location.pathname === `${basePath}/analytics`;
      }
      return location.pathname === item.path || location.pathname.startsWith(item.path);
    });
  };

  const isUserBusinessActive = () => isDropdownActive(userBusinessItems);
  const isSystemCatalogActive = () => isDropdownActive(systemCatalogItems);
  const isFinanceActive = () => isDropdownActive(financeItems);
  const isPromotionsActive = () => isDropdownActive(promotionsItems);
  const isCmsActive = () => isDropdownActive(cmsItems);
  const isSecurityActive = () => isDropdownActive(securityItems);
  const isSupportActive = () => isDropdownActive(supportItems);
  const isSystemActive = () => isDropdownActive(systemItems);

  const toggleDropdown = (key: string) => {
    setDropdowns(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Fetch profile data for header
  const fetchProfileData = async () => {
    // Check if token exists before making API call
    const token = localStorage.getItem('authToken');
    if (!token || !token.trim()) {
      console.warn('⚠️ No token found - skipping profile fetch');
      // Set fallback from localStorage
      const userEmail = localStorage.getItem('userEmail');
      setUserName(userEmail?.split('@')[0] || (role === 'superadmin' ? 'Super Admin' : 'Admin'));
      return;
    }

    try {
      setImageError(false);
      if (role === 'superadmin') {
        const profile = await superAdminService.getProfile();
        setProfileImage(profile.profile_image);
        setUserName(profile.full_name);
      } else {
        // For regular admin, fetch admin profile
        const profile = await adminService.getProfile();
        setProfileImage(profile.profile_image);
        setUserName(profile.name); // Admin profile uses 'name' field
      }
    } catch (error: any) {
      console.error('Error fetching profile:', error);
      // If 401 error, don't set fallback - let ProtectedRoute handle redirect
      if (error?.response?.status === 401) {
        console.warn('⚠️ 401 error - token may be invalid');
        return;
      }
      // Set fallback for other errors
      const userEmail = localStorage.getItem('userEmail');
      setUserName(userEmail?.split('@')[0] || (role === 'superadmin' ? 'Super Admin' : 'Admin'));
    }
  };

  // Fetch unread notification count (only for admin role)
  const fetchUnreadCount = async () => {
    if (role !== 'admin') return;

    try {
      const token = localStorage.getItem('authToken');
      if (!token || !token.trim()) return;

      const result = await adminService.getUnreadNotificationCount();
      setUnreadCount(result.count);
    } catch (error: any) {
      console.error('Error fetching unread count:', error);
      setUnreadCount(0);
    }
  };

  useEffect(() => {
    // Small delay to allow authToken to be written after login/refresh
    const timeoutId = setTimeout(() => {
      fetchProfileData();
      if (role === 'admin') {
        fetchUnreadCount();
      }
    }, 50);

    // Listen for profile image update events
    const handleProfileImageUpdate = () => {
      fetchProfileData();
    };

    window.addEventListener('profileImageUpdated', handleProfileImageUpdate);

    // Poll for unread count updates every 30 seconds (only for admin)
    let intervalId: ReturnType<typeof setInterval> | null = null;
    if (role === 'admin') {
      intervalId = setInterval(() => {
        fetchUnreadCount();
      }, 30000); // 30 seconds
    }

    // Listen for notification updates
    const handleNotificationUpdate = () => {
      if (role === 'admin') {
        fetchUnreadCount();
      }
    };

    window.addEventListener('notificationUpdated', handleNotificationUpdate);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('profileImageUpdated', handleProfileImageUpdate);
      window.removeEventListener('notificationUpdated', handleNotificationUpdate);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [role]);

  // Auto-open dropdowns if their routes are active
  useEffect(() => {
    if (role === 'admin') {
      setDropdowns({
        userBusiness: isUserBusinessActive(),
        systemCatalog: isSystemCatalogActive(),
        finance: isFinanceActive(),
        promotions: isPromotionsActive(),
        cms: isCmsActive(),
        security: isSecurityActive(),
        support: isSupportActive(),
        system: isSystemActive(),
      });
    }
  }, [location.pathname, role]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);


  const handleLogout = () => {
    // Clear authentication data
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('super_admin_token');
    localStorage.removeItem('authToken');

    // Redirect to login page
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Top Navigation */}
      <nav className="bg-white shadow-md fixed w-full top-0 z-40 border-b border-secondary-100">
        <div className="flex items-center justify-between h-20 px-4 md:px-8">
          {/* Logo & Menu Button */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden text-secondary-600 hover:text-primary-500"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <Link to={`${basePath}/dashboard`} className="flex items-center space-x-3">
              <img
                src="/rita_logo_2-removebg-preview.png"
                alt="Rita Logo"
                className="h-20 w-auto object-contain"
              />
              <div className="hidden sm:block">
                <span className="block text-xs text-secondary-500">
                  {role === 'superadmin' ? 'Super Admin' : 'Admin Panel'}
                </span>
              </div>
            </Link>
          </div>

          {/* Right Side */}
          <div className="flex items-center space-x-4">
            {/* Notification Icon - Only for admin role */}
            {role === 'admin' && (
              <Link
                to={`${basePath}/notifications`}
                className="relative p-2 text-secondary-600 hover:text-primary-500 hover:bg-secondary-50 rounded-lg transition-colors"
                title="Notifications"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 bg-primary-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px]">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
            )}

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 hover:bg-secondary-50 px-3 py-2 rounded-lg transition-colors"
              >
                <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-secondary-200 flex items-center justify-center bg-secondary-100 relative">
                  {profileImage && !imageError ? (
                    <img
                      src={getImageUrl(profileImage) || ''}
                      alt={userName}
                      onError={() => setImageError(true)}
                      className="w-full h-full object-cover absolute inset-0"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-white">
                      <User size={20} className="text-secondary-400" />
                    </div>
                  )}
                </div>
                <span className="hidden md:block font-semibold text-secondary-900">
                  {userName || (role === 'superadmin' ? 'Super Admin' : 'Admin')}
                </span>
                <ChevronDown size={16} className="text-secondary-600" />
              </button>

              {userMenuOpen && (
                <div
                  className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 animate-slide-down"
                  onMouseLeave={() => setUserMenuOpen(false)}
                >
                  <Link
                    to={`${basePath}/profile`}
                    onClick={() => setUserMenuOpen(false)}
                    className="block px-4 py-2 text-secondary-700 hover:bg-primary-50 hover:text-primary-600"
                  >
                    Profile
                  </Link>
                  <hr className="my-2" />
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-danger-600 hover:bg-danger-50 flex items-center space-x-2"
                  >
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar Overlay for Mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-secondary-900/50 z-25 lg:hidden backdrop-blur-sm transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-20 h-[calc(100vh-5rem)] w-64 bg-white shadow-lg border-r border-secondary-100 transform transition-transform duration-300 ease-in-out z-30 flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0`}
      >
        <nav className="p-5 space-y-3 flex-1 overflow-y-auto">
          {role === 'superadmin' ? (
            menuItems.slice(0, 3).map((item, index) => (
              <Link
                key={index}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all ${isActive(item.path)
                  ? 'bg-primary-500 text-white shadow-green'
                  : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                  }`}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            ))
          ) : (
            <div className="space-y-4">
              {/* Section: OVERVIEW */}
              <div className="space-y-1">
                <div className="px-4 py-1">
                  <span className="text-[10px] font-bold text-secondary-400 uppercase tracking-wider">
                    Overview
                  </span>
                </div>
                {/* Dashboard */}
                <Link
                  to={`${basePath}/dashboard`}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all ${isActive(`${basePath}/dashboard`)
                    ? 'bg-primary-500 text-white shadow-green'
                    : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                    }`}
                >
                  <LayoutDashboard size={20} />
                  <span>Dashboard</span>
                </Link>

                {/* Profile */}
                <Link
                  to={`${basePath}/profile`}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all ${isActive(`${basePath}/profile`)
                    ? 'bg-primary-500 text-white shadow-green'
                    : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                    }`}
                >
                  <User size={20} />
                  <span>Profile</span>
                </Link>

                {/* Notifications */}
                <Link
                  to={`${basePath}/notifications`}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-all ${isActive(`${basePath}/notifications`)
                    ? 'bg-primary-500 text-white shadow-green'
                    : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                    }`}
                >
                  <div className="flex items-center space-x-3">
                    <Bell size={20} />
                    <span>Notifications</span>
                  </div>
                  {unreadCount > 0 && (
                    <span className={`text-xs font-bold rounded-full h-5 px-2 flex items-center justify-center ${isActive(`${basePath}/notifications`) ? 'bg-white text-primary-600' : 'bg-primary-500 text-white'}`}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
              </div>

              {/* Section: CORE MANAGEMENT */}
              <div className="space-y-1">
                <div className="px-4 py-1">
                  <span className="text-[10px] font-bold text-secondary-400 uppercase tracking-wider">
                    Core Management
                  </span>
                </div>
                {/* Users & Businesses Dropdown */}
                <div className="space-y-1">
                  <button
                    onClick={() => toggleDropdown('userBusiness')}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-all ${isUserBusinessActive()
                      ? 'bg-primary-500 text-white shadow-green'
                      : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Users size={20} />
                      <span>User & Business</span>
                    </div>
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-200 ${dropdowns.userBusiness ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {dropdowns.userBusiness && (
                    <div className="ml-4 space-y-1 animate-slide-down">
                      {userBusinessItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2 rounded-lg font-medium transition-all text-sm ${isActive(item.path)
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                            }`}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* System Catalog Dropdown */}
                <div className="space-y-1">
                  <button
                    onClick={() => toggleDropdown('systemCatalog')}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-all ${isSystemCatalogActive()
                      ? 'bg-primary-500 text-white shadow-green'
                      : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Package size={20} />
                      <span>System Catalog</span>
                    </div>
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-200 ${dropdowns.systemCatalog ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {dropdowns.systemCatalog && (
                    <div className="ml-4 space-y-1 animate-slide-down">
                      {systemCatalogItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2 rounded-lg font-medium transition-all text-sm ${isActive(item.path)
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                            }`}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Section: FINANCIALS */}
              <div className="space-y-1">
                <div className="px-4 py-1">
                  <span className="text-[10px] font-bold text-secondary-400 uppercase tracking-wider">
                    Financials
                  </span>
                </div>
                {/* Finance Dropdown */}
                <div className="space-y-1">
                  <button
                    onClick={() => toggleDropdown('finance')}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-all ${isFinanceActive()
                      ? 'bg-primary-500 text-white shadow-green'
                      : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      <CreditCard size={20} />
                      <span>Finance & Settlements</span>
                    </div>
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-200 ${dropdowns.finance ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {dropdowns.finance && (
                    <div className="ml-4 space-y-1 animate-slide-down">
                      {financeItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2 rounded-lg font-medium transition-all text-sm ${isActive(item.path)
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                            }`}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Section: PROMOTIONS - Hidden from sidebar */}
              {/* Marketing & Offers section is accessible via direct URL only */}

              {/* Section: CMS */}
              <div className="space-y-1">
                <div className="px-4 py-1">
                  <span className="text-[10px] font-bold text-secondary-400 uppercase tracking-wider">
                    Content Management
                  </span>
                </div>
                {/* CMS Dropdown */}
                <div className="space-y-1">
                  <button
                    onClick={() => toggleDropdown('cms')}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-all ${isCmsActive()
                      ? 'bg-primary-500 text-white shadow-green'
                      : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      <FileText size={20} />
                      <span>CMS & Careers</span>
                    </div>
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-200 ${dropdowns.cms ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {dropdowns.cms && (
                    <div className="ml-4 space-y-1 animate-slide-down">
                      {cmsItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2 rounded-lg font-medium transition-all text-sm ${isActive(item.path)
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                            }`}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Section: SECURITY & SUPPORT */}
              <div className="space-y-1">
                <div className="px-4 py-1">
                  <span className="text-[10px] font-bold text-secondary-400 uppercase tracking-wider">
                    Security & Support
                  </span>
                </div>
                {/* Security Dropdown */}
                <div className="space-y-1">
                  <button
                    onClick={() => toggleDropdown('security')}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-all ${isSecurityActive()
                      ? 'bg-primary-500 text-white shadow-green'
                      : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Shield size={20} />
                      <span>Security & Audits</span>
                    </div>
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-200 ${dropdowns.security ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {dropdowns.security && (
                    <div className="ml-4 space-y-1 animate-slide-down">
                      {securityItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2 rounded-lg font-medium transition-all text-sm ${isActive(item.path)
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                            }`}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Support Dropdown */}
                <div className="space-y-1">
                  <button
                    onClick={() => toggleDropdown('support')}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-all ${isSupportActive()
                      ? 'bg-primary-500 text-white shadow-green'
                      : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      <MessageSquare size={20} />
                      <span>Support & Help</span>
                    </div>
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-200 ${dropdowns.support ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {dropdowns.support && (
                    <div className="ml-4 space-y-1 animate-slide-down">
                      {supportItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2 rounded-lg font-medium transition-all text-sm ${isActive(item.path)
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                            }`}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Section: SYSTEM */}
              <div className="space-y-1">
                <div className="px-4 py-1">
                  <span className="text-[10px] font-bold text-secondary-400 uppercase tracking-wider">
                    System Configuration
                  </span>
                </div>
                {/* System Dropdown */}
                <div className="space-y-1">
                  <button
                    onClick={() => toggleDropdown('system')}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-all ${isSystemActive()
                      ? 'bg-primary-500 text-white shadow-green'
                      : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Settings size={20} />
                      <span>Settings & Reports</span>
                    </div>
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-200 ${dropdowns.system ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {dropdowns.system && (
                    <div className="ml-4 space-y-1 animate-slide-down">
                      {systemItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2 rounded-lg font-medium transition-all text-sm ${isActive(item.path)
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                            }`}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </nav>

        {/* Logout at bottom */}
        <div className="p-5 border-t border-secondary-100">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 px-4 py-3 w-full text-danger-600 hover:bg-danger-50 rounded-lg font-medium transition-all"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`pt-20 transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-64'
          }`}
      >
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden backdrop-blur-sm transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
